import { isEmpty } from "lodash/fp";
import { DateTime } from "luxon";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import type { ConferencingMeetingPlatform } from "@readai/common/types";

import type { PublicRssLink } from "@app/booking/types/rssLinkTypes";
import {
  updateDateTimezone,
  updateDayTimezone,
  updateMonthTimezone,
} from "@app/booking/utils";

export const SUPPORTED_MEETING_PLATFORMS: {
  value: Exclude<ConferencingMeetingPlatform, "webex" | "mobile">;
  label: string;
}[] = [
  { value: "zoom", label: "Zoom" },
  { value: "meet", label: "Google Meet" },
  { value: "teams", label: "Microsoft Teams" },
];

interface SchedulingStoreStateValues {
  allowedPlatforms: (typeof SUPPORTED_MEETING_PLATFORMS)[number][];
  constraints: PublicRssLink | null;
  selectedDate: DateTime | null;
  selectedMonth: DateTime;
  selectedTime: DateTime | null;
  selectedTimezone: string | null;
}

interface SchedulingStoreState extends SchedulingStoreStateValues {
  /** Resets everything except for the selected timezone; used when the user
   * navigates backwards to the "choose duration" page */
  resetSelections: () => void;
  setConstraints: (constraints: PublicRssLink) => void;
  setSelectedDate: (selectedDate: DateTime | null) => void;
  setSelectedDateAndTime: (selectedTime: DateTime) => void;
  setSelectedMonth: (monthDate: DateTime) => void;
  setSelectedTime: (selectedTime: DateTime | null) => void;
  setSelectedTimezone: (selectedTimezone: string) => void;
}

const DEFAULT_STATE: SchedulingStoreStateValues = {
  allowedPlatforms: [],
  constraints: null,
  selectedDate: null,
  selectedMonth: DateTime.now(),
  selectedTime: null,
  selectedTimezone: DateTime.local().zoneName,
};

const getAllowedPlatforms = (
  constraints: PublicRssLink | null,
): SchedulingStoreStateValues["allowedPlatforms"] => {
  const allowedPlatformsConstraint = constraints?.allowedPlatforms;
  if (isEmpty(allowedPlatformsConstraint)) return [];

  if (allowedPlatformsConstraint?.includes("all")) {
    return SUPPORTED_MEETING_PLATFORMS;
  }
  return SUPPORTED_MEETING_PLATFORMS.filter((option) =>
    allowedPlatformsConstraint?.includes(option.value),
  );
};

const useSchedulingStore = createWithEqualityFn<SchedulingStoreState>(
  (set) => ({
    ...DEFAULT_STATE,
    resetSelections: () =>
      set((state) => ({
        ...DEFAULT_STATE,
        selectedTimezone: state.selectedTimezone,
      })),
    setConstraints: (constraints) =>
      set({
        constraints,
        allowedPlatforms: getAllowedPlatforms(constraints),
      }),
    setSelectedDate: (selectedDate) =>
      set((state) => ({
        selectedDate: state.selectedTimezone
          ? updateDayTimezone(selectedDate, state.selectedTimezone)
          : selectedDate,
        selectedMonth:
          // month can also change via the user clicking on a date outside the current month, so
          // update the month if that happens
          selectedDate && state.selectedMonth.month !== selectedDate.month
            ? selectedDate.startOf("month")
            : state.selectedMonth,
      })),
    setSelectedDateAndTime: (selectedTime) =>
      set((state) => ({
        selectedTime,
        selectedDate: selectedTime,
        selectedMonth:
          // month can also change via the user clicking on a date outside the current month, so
          // update the month if that happens
          selectedTime && state.selectedMonth.month !== selectedTime.month
            ? selectedTime.startOf("month")
            : state.selectedMonth,
      })),
    setSelectedMonth: (selectedMonth) =>
      set((state) => ({
        selectedMonth: state.selectedTimezone
          ? updateMonthTimezone(selectedMonth, state.selectedTimezone)
          : selectedMonth,
      })),
    setSelectedTime: (selectedTime) => set({ selectedTime }),
    setSelectedTimezone: (selectedTimezone) =>
      set((state) => ({
        selectedTimezone,
        // update all dates to use the new timezone
        selectedDate: updateDayTimezone(state.selectedDate, selectedTimezone),
        selectedMonth: updateMonthTimezone(
          state.selectedMonth,
          selectedTimezone,
        ),
        selectedTime: updateDateTimezone(state.selectedTime, selectedTimezone),
      })),
  }),
  shallow,
);

export default useSchedulingStore;
